import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  Chip,
  IconButton,
  Stack,
  TableSortLabel,
  Tooltip,
} from "@mui/material";
import { Box } from "@mui/system";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { useSelector } from "react-redux";
import { useCallback, useState } from "react";
import FMEStatus from "../helpers/components/FMEStatus";
import "../Receipts.css";

const FmeRecordsTable = ({ onUnpairClick }: any) => {
  const { selectedReceipt } = useSelector((state: any) => state.receipts);
  const [sort, setSort] = useState({
    keytoSort: "",
    direction: "desc",
  }) as any;
  const tableHeaders = [
    { id: 0, KEY: "fme_id", label: "FME", sortable: true },
    { id: 1, KEY: "debit", label: "Debit", sortable: true },
    { id: 2, KEY: "percent_covered", label: "% Covered", sortable: true },
    { id: 3, KEY: "fme_status", label: "FME Status", sortable: true },
  ];

  const getSortedData = useCallback(
    (arrayToSort: any[]) => {
      if (!sort.keytoSort || !sort.direction || !arrayToSort) {
        return arrayToSort;
      }

      // Helper function to get debit value using priority logic
      const getDebitValue = (row: any) => {
        return row?.manual_invoice_total_income != null
          ? row.manual_invoice_total_income
          : row?.invoice_total_income != null
          ? row.invoice_total_income
          : row?.total_income ?? 0;
      };

      // Helper function to calculate percent covered
      const getPercentCovered = (row: any) => {
        const debit = getDebitValue(row);
        const receiptAmount = selectedReceipt?.receipt_amount;

        if (debit != null && receiptAmount != null && receiptAmount !== 0) {
          return (debit / receiptAmount) * 100;
        }
        return 0;
      };

      return [...arrayToSort].sort((a, b) => {
        let aValue: any;
        let bValue: any;

        // Handle different column types
        switch (sort.keytoSort) {
          case "debit":
            aValue = getDebitValue(a);
            bValue = getDebitValue(b);
            break;
          case "percent_covered":
            aValue = getPercentCovered(a);
            bValue = getPercentCovered(b);
            break;
          case "fme_status":
            aValue = a?.status || "";
            bValue = b?.status || "";
            break;
          case "fme_id":
            aValue = a?.fme_id || "";
            bValue = b?.fme_id || "";
            break;
          default:
            aValue = a[sort.keytoSort];
            bValue = b[sort.keytoSort];
        }

        // Handle null/undefined values
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;

        // Numeric comparison
        if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        }

        // String comparison
        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();

        if (aStr < bStr) {
          return sort.direction === "asc" ? -1 : 1;
        }
        if (aStr > bStr) {
          return sort.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    },
    [sort, selectedReceipt?.receipt_amount]
  );

  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable
    setSort({
      keytoSort: header.KEY,
      direction:
        header.KEY === sort.keytoSort && sort.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  const sortedData = getSortedData(selectedReceipt?.fmes);

  return (
    <>
      <TableContainer className="receiptsTable" sx={{ my: 3 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {tableHeaders.map((header) => (
                <TableCell key={header.id}>
                  <TableSortLabel
                    active={sort.keytoSort === header.KEY}
                    direction={
                      sort.keytoSort === header.KEY ? sort.direction : "asc"
                    }
                    onClick={() => handleHeaderClick(header)}
                    disabled={!header.sortable}
                  >
                    {header.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData?.map((row: any) => (
              <React.Fragment key={row.cron_id}>
                <TableRow
                  sx={{
                    "& > *": {
                      borderBottom:
                        "1px solid rgba(224, 224, 224, 1) !important",
                    },
                  }}
                >
                  <TableCell align="left">
                    {/* FME ID Section */}
                    <div className="chip-data">
                      <>
                        <Chip
                          label={row?.fme_id ? row.fme_id : "N/A"}
                          sx={{
                            backgroundColor: "#ffffff",
                            color: "rgba(0, 0, 0, 0.87)",
                            borderRadius: "5px",
                            boxShadow: "1px 1px 2px 1px #00000030",
                            height: "26px",
                            fontWeight: "500",
                            cursor: "pointer",
                            border: "1px solid #ffffff",
                            mr: 1,
                            "&:hover": {
                              backgroundColor: "transparent",
                              color: "#9c27b0", // slightly darker on hover
                              border: "1px solid #9c27b0",
                            },
                          }}
                        />
                        <Tooltip title="Delete" placement="top">
                          <IconButton
                            sx={{ width: "40px", height: "40px" }}
                            onClick={() => onUnpairClick(row)}
                            data-tracker="594"
                          >
                            <LinkOffIcon color="secondary" fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    </div>
                  </TableCell>
                  <TableCell align="left">
                    {/* Debit Section */}
                    <Box display={"flex"} alignItems={"center"}>
                      {row?.manual_invoice_total_income != null
                        ? row.manual_invoice_total_income
                        : row?.invoice_total_income != null
                        ? row.invoice_total_income
                        : row?.total_income ?? "N/A"}{" "}
                      <Stack
                        sx={{
                          color: "rgba(0, 0, 0, 0.56)",
                          fontSize: "20px !important",
                          fontWeight: "400",
                          ml: 1,
                        }}
                      >
                        {row?.currency_symbol || ""}
                      </Stack>
                    </Box>
                  </TableCell>
                  <TableCell align="left">
                    {/* % Covered Section */}
                    {(() => {
                      // Get the debit value using the same priority logic
                      const debit =
                        row?.manual_invoice_total_income != null
                          ? row.manual_invoice_total_income
                          : row?.invoice_total_income != null
                          ? row.invoice_total_income
                          : row?.total_income;

                      const receiptAmount = selectedReceipt?.receipt_amount;

                      // Calculate percentage if both values are valid
                      if (
                        debit != null &&
                        receiptAmount != null &&
                        receiptAmount !== 0
                      ) {
                        const percentage = (debit / receiptAmount) * 100;
                        return `${percentage.toFixed(2)}%`;
                      }

                      return "N/A";
                    })()}
                  </TableCell>
                  <TableCell align="left">
                    {/* FME Status Section */}
                    <FMEStatus status={row?.status} />
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default FmeRecordsTable;
